import { yupResolver } from '@hookform/resolvers/yup';
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import { Currency } from '@/Models/Currency';
import { findJobCategoryByJobFunctionId } from '@/Models/JobCategory';
import ProcessingState from '@/Models/ProcessingState';
import YupValidationErrors from '@/Utils/YupValidationErrors';
import { JobDetails } from '../../Models/JobDetails';
import StepProps from '../StepProps';
import DirectReports from './Components/DirectReports';
import EmploymentType from './Components/EmploymentType';
import JobDescriptionLanguage from './Components/JobDescriptionLanguage';
import JobDetailsStepHeader from './Components/JobDetailsStepHeader';
import JobDetailsStepNavigation from './Components/JobDetailsStepNavigation';
import JobFunctionSelection from './Components/JobFunctionSelection';
import JobLocation from './Components/JobLocation/JobLocation';
import WorkArrangement from './Components/JobLocation/WorkArrangement';
import JobPostingOptions from './Components/JobPostingOptions';
import JobTitle from './Components/JobTitle';
import Salary from './Components/Salary';
import WorkExperience from './Components/WorkExperience';
import {
    JobDetailsFormValues,
    jobDetailsFormValues2Model,
    jobDetailsModel2FormValues,
    MAX_SALARY,
} from './JobDetailsStepUtils';
import { useAssessmentSettings } from '@/api/useAssessmentSettings';

interface JobDetailsStepProps extends StepProps {
    processingState: ProcessingState;
    jobDetails: JobDetails;
    updateJobDetails: (newJobDetails: JobDetails) => Promise<void>;
    currencies: Array<Currency>;
    client: ClientResponse;
    companyName: string;
    disableJobDescriptionLanguage?: boolean;
    disableJobFunctionSelection?: boolean;
    disableHowDoYouWantToPost?: boolean;
}

const validationScheme = yup.object().shape({
    assessmentName: yup
        .string()
        .required(YupValidationErrors.Required)
        .max(50, YupValidationErrors.JobTitleMaxSymbolsValidationError),
    jobLocation: yup.object().nullable().required(YupValidationErrors.Required),
    jobFunctionId: yup.string().required(YupValidationErrors.Required),
    jobCategoryId: yup.string().required(YupValidationErrors.Required),
    doNotDisplaySalary: yup.boolean(),
    yearlySalaryFrom: yup.mixed().when('doNotDisplaySalary', {
        is: false,
        then: yup
            .number()
            .typeError(YupValidationErrors.InvalidInputError)
            .positive(YupValidationErrors.NegativeValueError)
            .required(YupValidationErrors.EmptyFieldError)
            .max(MAX_SALARY, YupValidationErrors.SalaryExceedsSystemLimitError), // this is max number before system complains about precision
    }),
    yearlySalaryTo: yup.mixed().when('doNotDisplaySalary', {
        is: false,
        then: yup
            .number()
            .typeError(YupValidationErrors.InvalidInputError)
            .positive(YupValidationErrors.NegativeValueError)
            .required(YupValidationErrors.EmptyFieldError)
            .max(MAX_SALARY, YupValidationErrors.SalaryExceedsSystemLimitError) // this is max number before system complains about precision
            .when(['yearlySalaryFrom'], (yearlySalaryFrom, schema) => {
                return schema.test({
                    name: 'maxGreaterThanMin',
                    test(value?: number) {
                        if (yearlySalaryFrom !== undefined && value !== undefined) {
                            return value >= yearlySalaryFrom;
                        }
                        return true; // If either field is not present, don't perform the test.
                    },
                    message: YupValidationErrors.IncorrectSalaryLimitsError,
                });
            }),
    }),
    currency: yup.object().when('doNotDisplaySalary', {
        is: false,
        then: yup.object().shape({
            id: yup.string().required(YupValidationErrors.EmptyFieldError),
            label: yup.string(),
        }),
    }),
    jobDescriptionLanguage: yup.object().shape({
        id: yup.string().required(YupValidationErrors.Required),
        label: yup.string(),
    }),
    jobPostingType: yup.string().required(YupValidationErrors.Required),
});

const JobDetailsStep = ({
    activeStep,
    handleNext,
    handleBack,
    processingState,
    jobDetails,
    updateJobDetails,
    currencies,
    client,
    companyName,
    disableJobDescriptionLanguage = false,
    disableJobFunctionSelection = false,
    disableHowDoYouWantToPost = false,
}: JobDetailsStepProps) => {
    const { data: assessmentSettings } = useAssessmentSettings();
    const jobFunctionId = useMemo(() => {
        const jfid = jobDetails.jobFunctionId;
        if (!jfid || jfid <= 0) {
            return '';
        }

        const jobCategory = findJobCategoryByJobFunctionId(assessmentSettings?.jobCategories ?? [], jfid);
        return jobCategory != null ? `${jobCategory.id}` : '';
    }, [assessmentSettings?.jobCategories, jobDetails.jobFunctionId]);
    const form = useForm({
        defaultValues: jobDetailsModel2FormValues(jobFunctionId, jobDetails),
        // @ts-expect-error schema is incomplete
        resolver: yupResolver(validationScheme) as yup.SchemaOf<JobDetailsFormValues>,
        mode: 'all',
    });

    const submitHandlerAsync = async (values: JobDetailsFormValues) => {
        await updateJobDetails(jobDetailsFormValues2Model(values));

        if (handleNext) {
            handleNext();
        }
    };

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <JobDetailsStepHeader />
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(submitHandlerAsync)}>
                    <div className="border-border bg-alicia flex flex-col gap-7 rounded-lg border px-5 py-8">
                        <JobTitle />
                        <div className="flex flex-row gap-5">
                            <WorkArrangement />
                            <JobLocation />
                        </div>
                        {assessmentSettings && (
                            <JobFunctionSelection
                                assessmentConfiguration={assessmentSettings}
                                disabled={disableJobFunctionSelection}
                            />
                        )}
                        <div className="flex flex-row gap-5">
                            <WorkExperience />
                            <EmploymentType />
                        </div>
                        <DirectReports />
                        <Salary currencies={currencies} />
                        {!client.isDefault && (
                            <JobPostingOptions
                                companyName={companyName}
                                clientName={client.name}
                                disabled={disableHowDoYouWantToPost}
                            />
                        )}
                        <JobDescriptionLanguage disabled={disableJobDescriptionLanguage} />
                    </div>
                    <JobDetailsStepNavigation
                        processingState={processingState}
                        activeStep={activeStep}
                        handleBack={handleBack}
                    />
                </form>
            </FormProvider>
        </div>
    );
};

export default JobDetailsStep;
